import { Button } from '../Button/Button';
import { useForm } from 'react-hook-form';
import { HookedForm } from '../HookedForm';
import { AIDropdownMinimal } from '../AIDropdown/AIDropdownMinimal';
import { toast } from 'react-toastify';
import { useAppSelector } from '@app/redux/hooks';
import { useFeatureAccess } from '@app/hooks/useFeatureAccess';
import { fixGrammarAndSpelling, makeTextConcise, makeTextFormal, makeTextLonger, regenerateText } from '@app/services/service';
import { deleteReply, postReply, updateReply } from '@app/services/reviews';
import { getBusinessInfo } from '@app/services/business';
import EditReplyIcon from '@app/assets/icons/edit-reply.svg?react';
import TrashReplyIcon from '@app/assets/icons/trash-reply.svg?react';
import DotHorizontalIcon from '@app/assets/icons/dot-horizontal.svg?react';
import RegenerateIcon from '@app/assets/icons/ai/regenerate.svg?react';
import GrammerIcon from '@app/assets/icons/ai/grammar.svg?react';
import ConciseIcon from '@app/assets/icons/ai/concise.svg?react';
import LongerIcon from '@app/assets/icons/ai/text-longer.svg?react';
import FormatIcon from '@app/assets/icons/ai/format.svg?react';
import GoogleLogo from '@app/assets/icons/Google.svg?react';
import FullStarIcon from '@app/assets/icons/star-full.svg?react';
import EmptyStarIcon from '@app/assets/icons/star-empty.svg?react';
import YelpIcon from '@app/assets/icons/integrations/yelp.svg?react';
import EchoLogo from '@app/assets/simple-logo.svg?react';
import { BusinessReviews, ExternalReview } from '@app/models/reviews';
import { getTimeAgo } from '@app/utils/utils';
import { useEffect, useRef, useState } from 'react';
import { Tooltip } from '@app/components/Tooltip/Tooltip';
import { IntegrationLabels, IntegrationType } from '@app/utils/consts.tsx';

const isRecentReview = (date: Date) => {
  const oneDayAgo = new Date();
  oneDayAgo.setDate(oneDayAgo.getDate() - 1);
  return date > oneDayAgo;
};

export const ReviewBox = ({ review }: { review: ExternalReview; onReply?: (review: BusinessReviews) => void }) => {
  const reviewAnswerForm = useForm<{ answer: string }>({
    defaultValues: {
      answer: '',
    },
  });
  const [isEditingReply, setIsEditingReply] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [editedReply, setEditedReply] = useState(review?.reply || '');
  const [isUpdatingReply, setIsUpdatingReply] = useState(false);
  const [isDeletingReply, setIsDeletingReply] = useState(false);
  const [isPostingReply, setIsPostingReply] = useState(false);
  const [localReply, setLocalReply] = useState<string | null>(review?.reply || '');
  const [businessDetails, setBusinessDetails] = useState<any>(null);
  const [isLoadingBusiness, setIsLoadingBusiness] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Get current business plan type to check for AI feature access
  const businessState = useAppSelector((state) => state.business);
  const { aiEnabled } = useFeatureAccess();

  // Show AI features only when enabled
  const showAIFeatures = aiEnabled;

  const createReviewContextText = (responseText: string) => {
    const customerName = review?.reviewerName?.trim();
    const platform = IntegrationLabels[review.sourceType];

    return `Review from ${customerName} (${review?.rating}/5 stars${platform !== 'internal' ? ` on ${platform}` : ''}): "${review?.review || ''}"

Current response: "${responseText}"`;
  };

  const actions = [
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammerIcon />,
      onClick: () => {
        const responseText = reviewAnswerForm.getValues('answer');
        const contextText = createReviewContextText(responseText);
        fixGrammarAndSpelling(contextText, undefined, true).then((response) => {
          reviewAnswerForm.setValue('answer', response?.data?.text);
        });
      },
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => {
        const responseText = reviewAnswerForm.getValues('answer');
        const contextText = createReviewContextText(responseText);
        makeTextConcise(contextText, undefined, true).then((response) => {
          reviewAnswerForm.setValue('answer', response?.data?.text);
        });
      },
    },
    {
      label: 'Make it longer',
      Icon: <LongerIcon />,
      onClick: () => {
        const responseText = reviewAnswerForm.getValues('answer');
        const contextText = createReviewContextText(responseText);
        makeTextLonger(contextText, undefined, true).then((response) => {
          reviewAnswerForm.setValue('answer', response?.data?.text);
        });
      },
    },
    {
      label: 'Formalize',
      Icon: <FormatIcon />,
      onClick: () => {
        const responseText = reviewAnswerForm.getValues('answer');
        const contextText = createReviewContextText(responseText);
        makeTextFormal(contextText, undefined, true).then((response) => {
          reviewAnswerForm.setValue('answer', response?.data?.text);
        });
      },
    },
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => {
        regenerateText(reviewAnswerForm.getValues('answer'), undefined, true).then((response) => {
          reviewAnswerForm.setValue('answer', response?.data?.text);
        });
      },
    },
    { label: 'Divider' },
  ];

  const handleShare = () => {
    let reviewUrl = '';
    // Use reviewUrl from props if available
    if (review?.reviewUrl) {
      reviewUrl = review.reviewUrl;
    }
    // else if ((review?.platform ?? 'internal') === 'yelp' && review?.url) {
    //   // For Yelp reviews, use the URL from API
    //   reviewUrl = review.url;
    // } else if ((review?.platform ?? 'internal') === 'yelp' && review?.id && review?.yelpBusinessId) {
    //   // Fallback for Yelp if no direct URL
    //   reviewUrl = `https://www.yelp.com/biz/${review.yelpBusinessId}?hrid=${review.id}`;
    // }

    if (reviewUrl) {
      navigator.clipboard
        .writeText(reviewUrl)
        .then(() => {
          toast.success('Review URL copied to clipboard!');
        })
        .catch(() => {
          toast.error('Failed to copy review URL.');
        });
    } else {
      toast.error('Review URL not available for sharing.');
    }
  };

  const handleEditReply = () => {
    setIsEditingReply(true);
    setEditedReply(review?.reply || '');
    setIsMenuOpen(false);
  };

  const handleDeleteReply = async () => {
    setIsDeletingReply(true);
    try {
      await deleteReply(review.id);
      toast.success('Reply deleted successfully');
      setLocalReply(null); // Clear local reply state
      reviewAnswerForm.reset();
    } catch (error) {
      toast.error('Failed to delete reply');
      console.error('Error deleting reply:', error);
    } finally {
      setIsDeletingReply(false);
      setIsMenuOpen(false);
    }
  };

  const handleSaveReply = async () => {
    if (!editedReply.trim()) {
      toast.error('Reply cannot be empty');
      return;
    }

    setIsUpdatingReply(true);
    try {
      await updateReply(review.id, editedReply.trim());
      toast.success('Reply updated successfully');
      setLocalReply(editedReply.trim()); // Update local reply state
      setIsEditingReply(false);
    } catch (error) {
      toast.error('Failed to update reply');
      console.error('Error updating reply:', error);
    } finally {
      setIsUpdatingReply(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingReply(false);
    setEditedReply(review?.reply || '');
  };

  const handlePostReply = async (data: { answer: string }) => {
    if (!data.answer?.trim()) {
      toast.error('Reply cannot be empty');
      return;
    }

    setIsPostingReply(true);
    try {
      await postReply(review.id, data.answer.trim());
      toast.success('Reply posted successfully');
      setLocalReply(data.answer.trim()); // Set the local reply to show the posted response
      reviewAnswerForm.reset();
    } catch (error) {
      toast.error('Failed to post reply');
      console.error('Error posting reply:', error);
    } finally {
      setIsPostingReply(false);
    }
  };

  // Fetch business details if not available in review
  const fetchBusinessDetails = async () => {
    if (!review?.business?.photo?.url && !businessDetails && !isLoadingBusiness) {
      setIsLoadingBusiness(true);
      try {
        const response = await getBusinessInfo();
        setBusinessDetails(response?.data);
      } catch (error) {
        console.error('Error fetching business details:', error);
      } finally {
        setIsLoadingBusiness(false);
      }
    }
  };

  // Sync local reply with review prop changes
  useEffect(() => {
    setLocalReply(review?.reply || '');
  }, [review?.reply, review?.id]);

  // Fetch business details when component mounts
  useEffect(() => {
    fetchBusinessDetails();
  }, [review?.business?.photo?.url, businessDetails, isLoadingBusiness]);

  // Handle clicks outside the menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  return (
    <div className="bg-white p-3 sm:p-4 rounded-lg space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-base-antiFlash flex items-center justify-center flex-shrink-0">
          {review?.sourceType === IntegrationType.YELP ? (
            <YelpIcon width={24} height={24} className="sm:w-[30px] sm:h-[30px]" />
          ) : review?.sourceType === IntegrationType.GOOGLE ? (
            <GoogleLogo style={{ width: 24, height: 24 }} className="sm:w-[30px] sm:h-[30px]" />
          ) : (
            <EchoLogo style={{ width: 24, height: 24 }} className="sm:w-[30px] sm:h-[30px]" />
          )}
        </div>
        <div className="mr-auto space-y-1 min-w-0 flex-1">
          <h4 className="font-semibold text-sm sm:text-base truncate">
            {review?.reviewerName || 'Unknown'}
            {isRecentReview(new Date(review?.createTime ?? '')) && <span className="bg-system-successLight text-system-success p-1 text-xs rounded-sm ml-2">New</span>}
          </h4>

          <p className="text-xs sm:text-sm text-base-coolGray">
            Left a review {getTimeAgo(new Date(review?.createTime ?? ''))}
            {/*{review?.photos?.length > 0 && ` | ${review?.photos?.length} photos`}*/}
          </p>
        </div>

        {/* Share Button - Only show if review has URL to share */}
        {review?.reviewUrl && (
          <Tooltip content="Share this review">
            <button
              onClick={handleShare}
              className="px-3 py-2 sm:px-4 sm:py-2 rounded-lg bg-base-antiFlash hover:bg-base-ghostWhite transition-colors text-xs sm:text-sm font-medium text-base-davyGray flex-shrink-0"
              title="Share review"
              aria-label="Share review"
            >
              Share
            </button>
          </Tooltip>
        )}
      </div>
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, index) =>
          index < (review?.rating || 0) ? <FullStarIcon key={index} width={14} height={14} className="sm:w-4 sm:h-4" /> : <EmptyStarIcon key={index} width={14} height={14} className="sm:w-4 sm:h-4" />
        )}
      </div>
      <div>
        <p className="text-sm sm:text-base leading-relaxed">{review?.review}</p>
      </div>
      {/*{review?.photos && review?.photos.length > 0 && (*/}
      {/*  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">*/}
      {/*    {review?.photos.map((photo) => (*/}
      {/*      <div key={photo?.id} className="w-full aspect-square relative rounded-[4px] overflow-hidden">*/}
      {/*        <img alt="review attached" src={photo?.url} className="absolute w-full h-full object-cover object-center" />*/}
      {/*      </div>*/}
      {/*    ))}*/}
      {/*  </div>*/}
      {/*)}*/}
      {localReply || (review?.replyUpdateTime && localReply !== null) ? (
        <div className="bg-base-seasalt p-3 sm:p-4 border-t border-t-base-ghostWhite">
          <div className="flex items-start gap-3">
            {/* Business Logo */}
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm overflow-hidden">
              {review?.business?.photo?.url || businessDetails?.photo?.url ? (
                <img
                  src={review?.business?.photo?.url || businessDetails?.photo?.url}
                  alt={`${review?.business?.name || businessDetails?.name || businessState?.selected?.name || 'Business'} logo`}
                  className="w-full h-full object-cover"
                />
              ) : isLoadingBusiness ? (
                <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              ) : review?.sourceType === IntegrationType.YELP ? (
                <YelpIcon width={24} height={24} />
              ) : review?.sourceType === IntegrationType.GOOGLE ? (
                <GoogleLogo style={{ width: 24, height: 24 }} />
              ) : (
                <EchoLogo style={{ width: 24, height: 24 }} />
              )}
            </div>

            <div className="flex-1 min-w-0">
              {/* Business Name and Reply Info */}
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-semibold text-sm text-gray-900">{review?.business?.name || businessDetails?.name || businessState?.selected?.name || 'Business'}</h4>
                  <p className="text-xs text-gray-500">Replied to a review {getTimeAgo(new Date(review?.replyUpdateTime || review?.updateTime || ''))}</p>
                </div>

                {/* Menu Button */}
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => {
                      setIsMenuOpen(!isMenuOpen);
                    }}
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <DotHorizontalIcon width={16} height={16} />
                  </button>

                  {/* Dropdown Menu */}
                  {isMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[140px]">
                      <button onClick={handleEditReply} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg">
                        <EditReplyIcon width={16} height={16} />
                        Edit reply
                      </button>
                      <button onClick={handleDeleteReply} disabled={isDeletingReply} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg">
                        <TrashReplyIcon width={16} height={16} />
                        {isDeletingReply ? 'Deleting...' : 'Delete reply'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Reply Content */}
              {isEditingReply ? (
                <div className="space-y-3">
                  <textarea
                    value={editedReply}
                    onChange={(e) => setEditedReply(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900 resize-none text-sm focus:outline-none"
                    rows={3}
                    placeholder="Enter your reply..."
                  />
                  <div className="flex gap-2">
                    <button onClick={handleSaveReply} disabled={isUpdatingReply} className="px-4 py-2 bg-[#52B6FE] text-white rounded-lg text-sm font-medium disabled:opacity-50">
                      {isUpdatingReply ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={handleCancelEdit} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-3">
                  <p className="text-sm text-gray-900 leading-relaxed">{localReply}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-base-seasalt p-3 sm:p-4 border-t border-t-base-ghostWhite">
          <HookedForm formHook={reviewAnswerForm} onSubmit={handlePostReply} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-base-seasalt rounded-full flex items-center justify-center flex-shrink-0">
              {review?.sourceType === IntegrationType.YELP ? (
                <YelpIcon width={24} height={24} className="sm:w-[30px] sm:h-[30px]" />
              ) : review?.sourceType === IntegrationType.GOOGLE ? (
                <GoogleLogo style={{ width: 24, height: 24 }} className="sm:w-[30px] sm:h-[30px]" />
              ) : (
                <EchoLogo style={{ width: 24, height: 24 }} className="sm:w-[30px] sm:h-[30px]" />
              )}
            </div>
            <div className="flex flex-col gap-3 sm:gap-4 w-full">
              <textarea
                {...reviewAnswerForm.register('answer')}
                className="bg-white rounded py-3 px-4 w-full resize-y focus:outline-none"
                placeholder={`Respond to ${review?.reviewerName || 'Unknown'}`}
                rows={7}
              />
              <div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-4">
                {showAIFeatures && <AIDropdownMinimal actions={actions} />}
                {review?.sourceType === IntegrationType.YELP ? (
                  reviewAnswerForm.watch('answer') && reviewAnswerForm.watch('answer').trim() !== '' ? (
                    <a href={`https://biz.yelp.com/biz/${review?.yelpBusinessId}/review/${review?.id}`} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                      <Button type="button" className="w-full sm:w-auto text-xs sm:text-sm">
                        Respond on Yelp
                      </Button>
                    </a>
                  ) : (
                    <Button type="button" disabled className="w-full sm:w-auto text-xs sm:text-sm">
                      Respond on Yelp
                    </Button>
                  )
                ) : (
                  <Button type="submit" disabled={isPostingReply} loading={isPostingReply} className="w-full sm:w-auto text-xs sm:text-sm">
                    {isPostingReply ? 'Posting...' : 'Post response'}
                  </Button>
                )}
              </div>
            </div>
          </HookedForm>
        </div>
      )}
    </div>
  );
};
